import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "en" | "hi" | "gu";

const DICT = {
  en: {
    appName: "Jain Gaurav Yatra",
    welcome: "Jai Jinendra",
    login: "Login",
    logout: "Logout",
    phone: "Phone Number",
    sendOtp: "Send OTP",
    enterOtp: "Enter OTP",
    verify: "Verify & Continue",
    home: "Home",
    temples: "Temples",
    map: "Map",
    bookings: "Bookings",
    profile: "Profile",
    certificate: "Certificate",
    scanQr: "Scan QR",
    progress: "Yatra Progress",
    nextTemple: "Next Temple",
    visited: "Visited",
    pending: "Pending",
    viewDetails: "View Details",
    navigate: "Navigate",
    bookRickshaw: "Book Rickshaw",
    bookVolunteer: "Book Volunteer Guide",
    history: "History",
    bookNow: "Book Now",
    myBookings: "My Bookings",
    confirm: "Confirm Booking",
    language: "Language",
    admin: "Admin Portal",
    openApp: "Open App",
    save: "Save",
    cancel: "Cancel",
    back: "Back",
    loading: "Loading...",
    history_title: "Temple History",
    checkIn: "Check In",
    alreadyVisited: "Already Visited",
    noBookings: "No bookings yet",
    moolNayak: "Mool Nayak",
    established: "Established",
    selectDate: "Date",
    selectTime: "Time",
    notes: "Notes (optional)",

    // Landing — header/nav
    nav_about: "About",
    nav_features: "Features",
    nav_how: "How",
    nav_faq: "FAQ",

    // Landing — hero
    hero_chip: "Together for Dharma · Seva · Unity",
    hero_title_1: "Jain Gaurav",
    hero_title_2: "Yatra",
    hero_desc: "Stay connected with the Jain Gaurav Yatra — updates, routes, events, seva, announcements and community participation across Ahmedabad's 31 Derasars, all beautifully organized.",
    hero_cta_primary: "Open Member App",
    hero_cta_secondary: "View Yatra Details",
    hero_badge_route: "Live Route",
    hero_badge_route_sub: "Updated 2m ago",
    hero_badge_announce: "New Announcement",
    hero_badge_announce_sub: "Aarti at 6:30 AM",

    // About section
    about_chip: "About the Yatra",
    about_title: "A journey of devotion, seva and unity",
    about_desc: "The Jain Gaurav Yatra is a spiritual and community-driven journey celebrating Jain values, cultural pride and the timeless principles of ahimsa and seva.",
    about_b1: "A spiritual and community-driven journey",
    about_b2: "Promoting Jain values, unity, seva and cultural pride",
    about_b3: "Connecting devotees, volunteers and organizers",
    about_b4: "Easy access to all Yatra information through the app",
    about_overlay_kicker: "A spiritual journey",
    about_overlay_title: "Walk together. Pray together.",

    // Features
    features_chip: "Why Use This App",
    features_title: "Everything for the Yatra, in one place",
    features_desc: "No more scattered messages — find every update, schedule and announcement instantly.",
    f1_title: "Temple Directory",
    f1_desc: "View all 31 temples with details, history and significance.",
    f2_title: "Interactive Map",
    f2_desc: "Navigate temples easily with route guidance and locations.",
    f3_title: "Scan QR Code",
    f3_desc: "Quick check-in at every temple by scanning the QR code.",
    f4_title: "Certificates",
    f4_desc: "View achievements and earn certificates for your darshan journey.",
    f5_title: "Favorites",
    f5_desc: "Save your favorite temples for quick access anytime.",
    f6_title: "Rickshaw Information",
    f6_desc: "Easily book a rickshaw to travel between temples.",
    f7_title: "Volunteer Information",
    f7_desc: "Request a volunteer guide to assist your Yatra journey.",

    // Steps
    steps_chip: "How it Works",
    steps_title: "Get started in three simple steps",
    s1_title: "Open the App",
    s1_desc: "Open Jain Gaurav Yatra App right from this website — no installation needed.",
    s2_title: "Explore the App",
    s2_desc: "Browse temples, volunteers and rickshaws — instantly, no sign-up needed.",
    s3_title: "Stay Connected",
    s3_desc: "Get all Yatra updates, schedules, routes and announcements instantly.",

    // App preview
    preview_chip: "App Screens Preview",
    preview_title: "A glimpse inside the app",
    preview_desc: "Take a tour through the key screens that bring the Yatra experience to life.",
    screen_home: "Home Dashboard",
    screen_directory: "Temple Directory",
    screen_certificate: "Certificates",
    screen_bookings: "My Bookings",
    screen_volunteer: "Volunteer Guide",

    // Benefits
    benefits_chip: "Benefits for Users",
    benefits_title: "Made for the way you participate",
    benefits_desc: "From organizers to first-time devotees — simple, fast and trustworthy.",
    b1: "No need to depend on scattered WhatsApp messages",
    b2: "All information available in one place",
    b3: "Better coordination between organizers and participants",
    b4: "Real-time communication with the community",
    b5: "Easy access to routes, timings and updates",
    b6: "Helps build a connected Jain community",

    // Banner
    banner_title: "United in Faith, Service and Devotion",
    banner_desc: "The Jain Gaurav Yatra App is designed to bring the community together by making participation, communication and coordination simple and organized.",

    // Testimonials
    test_chip: "Community Voices",
    test_title: "Loved by our community",
    t1_quote: "I never miss a single aarti or pravachan. Everything I need is right on the home screen — beautifully organized.",
    t1_name: "Rakesh Shah",
    t1_role: "Yatra Participant",
    t2_quote: "Seva registration was effortless. The notifications keep our team perfectly coordinated through the day.",
    t2_name: "Priya Mehta",
    t2_role: "Volunteer",
    t3_quote: "Communicating with thousands of devotees has never been easier. A true blessing for the community.",
    t3_name: "Mahendrabhai Jain",
    t3_role: "Organizer",

    // FAQ
    faq_chip: "FAQ",
    faq_title: "Frequently asked questions",
    faq_q1: "What is Jain Gaurav Yatra App?",
    faq_a1: "It's the official companion app for the Jain Gaurav Yatra — a 31-temple darshan journey across Ahmedabad, with routes, schedules, seva and announcements.",
    faq_q2: "Who can use this app?",
    faq_a2: "Anyone — devotees, participants, volunteers, organizers and well-wishers from the Jain community can use the app.",
    faq_q3: "How will I receive Yatra updates?",
    faq_a3: "All real-time announcements appear inside the app and as instant push notifications.",
    faq_q4: "Can I register as a volunteer?",
    faq_a4: "Yes, the Volunteer section lets you register and pick your preferred temple and time slot.",
    faq_q5: "Is the app free to use?",
    faq_a5: "Yes, the app is completely free for the entire Jain community.",
    faq_q6: "How can I contact the organizers?",
    faq_a6: "Use the Contact Organizer button below or call the helpline on the footer.",

    // Final CTA
    cta_title: "Be a Part of Jain Gaurav Yatra",
    cta_desc: "Open the app and stay connected with every update, event and moment of the Yatra.",
    cta_open: "Open App",
    cta_contact: "Contact Organizer",

    // Footer
    footer_tagline: "Together for Dharma, Seva and Unity — your connected companion for the Yatra across Ahmedabad's 31 Derasars.",
    footer_quick: "Quick Links",
    footer_schedule: "Yatra Schedule",
    footer_contact: "Contact",
    footer_get: "Get the App",
    footer_copy: "© 2026 Jain Gaurav Yatra. Made with devotion for the community.",
    footer_location: "Ahmedabad, Gujarat",
  },

  hi: {
    appName: "जैन गौरव यात्रा",
    welcome: "जय जिनेन्द्र",
    login: "लॉगिन",
    logout: "लॉगआउट",
    phone: "मोबाइल नंबर",
    sendOtp: "OTP भेजें",
    enterOtp: "OTP डालें",
    verify: "सत्यापित करें",
    home: "होम",
    temples: "मंदिर",
    map: "नक्शा",
    bookings: "बुकिंग",
    profile: "प्रोफाइल",
    certificate: "प्रमाण पत्र",
    scanQr: "QR स्कैन",
    progress: "यात्रा प्रगति",
    nextTemple: "अगला मंदिर",
    visited: "दर्शन हुए",
    pending: "बाकी",
    viewDetails: "विवरण",
    navigate: "रास्ता",
    bookRickshaw: "रिक्शा बुक करें",
    bookVolunteer: "स्वयंसेवक बुक करें",
    history: "इतिहास",
    bookNow: "अभी बुक करें",
    myBookings: "मेरी बुकिंग",
    confirm: "बुकिंग पक्की करें",
    language: "भाषा",
    admin: "एडमिन पोर्टल",
    openApp: "ऐप खोलें",
    save: "सहेजें",
    cancel: "रद्द करें",
    back: "वापस",
    loading: "लोड हो रहा है...",
    history_title: "मंदिर का इतिहास",
    checkIn: "चेक इन",
    alreadyVisited: "पहले दर्शन हो चुके",
    noBookings: "अभी कोई बुकिंग नहीं",
    moolNayak: "मूलनायक",
    established: "स्थापना",
    selectDate: "तारीख",
    selectTime: "समय",
    notes: "टिप्पणी (वैकल्पिक)",

    nav_about: "परिचय",
    nav_features: "विशेषताएँ",
    nav_how: "कैसे काम करता है",
    nav_faq: "प्रश्नोत्तर",

    hero_chip: "धर्म · सेवा · एकता के साथ",
    hero_title_1: "जैन गौरव",
    hero_title_2: "यात्रा",
    hero_desc: "जैन गौरव यात्रा से जुड़े रहें — अहमदाबाद के 31 देरासरों की हर अपडेट, मार्ग, कार्यक्रम, सेवा और घोषणाएँ एक ही ऐप में सुंदर ढंग से।",
    hero_cta_primary: "मेंबर ऐप खोलें",
    hero_cta_secondary: "यात्रा विवरण देखें",
    hero_badge_route: "लाइव रूट",
    hero_badge_route_sub: "2 मिनट पहले अपडेट",
    hero_badge_announce: "नई घोषणा",
    hero_badge_announce_sub: "आरती सुबह 6:30 बजे",

    about_chip: "यात्रा के बारे में",
    about_title: "भक्ति, सेवा और एकता की यात्रा",
    about_desc: "जैन गौरव यात्रा एक आध्यात्मिक और सामुदायिक यात्रा है जो जैन मूल्यों, सांस्कृतिक गौरव और अहिंसा-सेवा के शाश्वत सिद्धांतों का उत्सव है।",
    about_b1: "एक आध्यात्मिक और सामुदायिक यात्रा",
    about_b2: "जैन मूल्यों, एकता, सेवा और सांस्कृतिक गौरव का प्रचार",
    about_b3: "भक्तों, स्वयंसेवकों और आयोजकों को जोड़ना",
    about_b4: "ऐप के माध्यम से सभी यात्रा जानकारी तक आसान पहुँच",
    about_overlay_kicker: "एक आध्यात्मिक यात्रा",
    about_overlay_title: "साथ चलें। साथ प्रार्थना करें।",

    features_chip: "इस ऐप का उपयोग क्यों",
    features_title: "यात्रा के लिए सब कुछ, एक ही जगह",
    features_desc: "अब बिखरे संदेशों की ज़रूरत नहीं — हर अपडेट, कार्यक्रम और घोषणा तुरंत पाएं।",
    f1_title: "मंदिर सूची",
    f1_desc: "सभी 31 मंदिरों का विवरण, इतिहास और महत्व देखें।",
    f2_title: "इंटरैक्टिव नक्शा",
    f2_desc: "रूट और स्थानों के साथ मंदिरों तक आसानी से पहुँचें।",
    f3_title: "QR कोड स्कैन",
    f3_desc: "QR स्कैन कर हर मंदिर पर त्वरित चेक-इन करें।",
    f4_title: "प्रमाण पत्र",
    f4_desc: "अपनी दर्शन यात्रा की उपलब्धियाँ और प्रमाण पत्र देखें।",
    f5_title: "पसंदीदा",
    f5_desc: "अपने प्रिय मंदिरों को कभी भी जल्दी एक्सेस करने के लिए सहेजें।",
    f6_title: "रिक्शा जानकारी",
    f6_desc: "मंदिरों के बीच यात्रा हेतु आसानी से रिक्शा बुक करें।",
    f7_title: "स्वयंसेवक जानकारी",
    f7_desc: "यात्रा में सहायता हेतु स्वयंसेवक गाइड का अनुरोध करें।",

    steps_chip: "यह कैसे काम करता है",
    steps_title: "तीन आसान चरणों में शुरू करें",
    s1_title: "ऐप खोलें",
    s1_desc: "इस वेबसाइट से सीधे जैन गौरव यात्रा ऐप खोलें — किसी इंस्टॉलेशन की ज़रूरत नहीं।",
    s2_title: "ऐप का अन्वेषण करें",
    s2_desc: "मंदिर, स्वयंसेवक और रिक्शा देखें — तुरंत, बिना साइन-अप।",
    s3_title: "जुड़े रहें",
    s3_desc: "हर यात्रा अपडेट, कार्यक्रम, मार्ग और घोषणा तुरंत प्राप्त करें।",

    preview_chip: "ऐप स्क्रीन झलक",
    preview_title: "ऐप के अंदर एक नज़र",
    preview_desc: "उन प्रमुख स्क्रीन्स की झलक देखें जो यात्रा अनुभव को जीवंत बनाती हैं।",
    screen_home: "होम डैशबोर्ड",
    screen_directory: "मंदिर सूची",
    screen_certificate: "प्रमाण पत्र",
    screen_bookings: "मेरी बुकिंग",
    screen_volunteer: "स्वयंसेवक गाइड",

    benefits_chip: "उपयोगकर्ताओं के लिए लाभ",
    benefits_title: "आपकी सहभागिता के लिए बना",
    benefits_desc: "आयोजकों से लेकर पहली बार के भक्तों तक — सरल, तेज़ और भरोसेमंद।",
    b1: "बिखरे WhatsApp संदेशों पर निर्भर रहने की ज़रूरत नहीं",
    b2: "सभी जानकारी एक ही जगह उपलब्ध",
    b3: "आयोजकों और प्रतिभागियों के बीच बेहतर समन्वय",
    b4: "समुदाय के साथ रीयल-टाइम संवाद",
    b5: "रूट, समय और अपडेट तक आसान पहुँच",
    b6: "जुड़े हुए जैन समुदाय के निर्माण में मदद",

    banner_title: "आस्था, सेवा और भक्ति में एकजुट",
    banner_desc: "जैन गौरव यात्रा ऐप समुदाय को एक साथ लाने के लिए बनाया गया है, जिससे सहभागिता, संवाद और समन्वय सरल और व्यवस्थित बनें।",

    test_chip: "समुदाय की आवाज़ें",
    test_title: "हमारे समुदाय द्वारा सराहा गया",
    t1_quote: "अब कोई आरती या प्रवचन नहीं छूटता। होम स्क्रीन पर सब कुछ खूबसूरती से व्यवस्थित है।",
    t1_name: "राकेश शाह",
    t1_role: "यात्रा प्रतिभागी",
    t2_quote: "सेवा पंजीकरण आसान था। सूचनाओं से हमारी टीम पूरे दिन समन्वित रहती है।",
    t2_name: "प्रिया मेहता",
    t2_role: "स्वयंसेवक",
    t3_quote: "हज़ारों भक्तों से संवाद करना अब बहुत आसान है। समुदाय के लिए सच्चा वरदान।",
    t3_name: "महेन्द्रभाई जैन",
    t3_role: "आयोजक",

    faq_chip: "प्रश्नोत्तर",
    faq_title: "अक्सर पूछे जाने वाले प्रश्न",
    faq_q1: "जैन गौरव यात्रा ऐप क्या है?",
    faq_a1: "यह जैन गौरव यात्रा का आधिकारिक साथी ऐप है — अहमदाबाद के 31 मंदिरों की दर्शन यात्रा, उसके मार्ग, कार्यक्रम, सेवा और घोषणाएँ।",
    faq_q2: "इस ऐप का उपयोग कौन कर सकता है?",
    faq_a2: "जैन समुदाय के सभी भक्त, प्रतिभागी, स्वयंसेवक, आयोजक और शुभचिंतक इसका उपयोग कर सकते हैं।",
    faq_q3: "मुझे यात्रा अपडेट कैसे मिलेंगे?",
    faq_a3: "सभी रीयल-टाइम घोषणाएँ ऐप के अंदर और तुरंत पुश सूचनाओं के रूप में मिलेंगी।",
    faq_q4: "क्या मैं स्वयंसेवक के रूप में पंजीकरण कर सकता हूँ?",
    faq_a4: "हाँ, स्वयंसेवक अनुभाग में आप पंजीकरण कर अपना मंदिर और समय चुन सकते हैं।",
    faq_q5: "क्या यह ऐप मुफ़्त है?",
    faq_a5: "हाँ, यह ऐप पूरे जैन समुदाय के लिए पूरी तरह मुफ़्त है।",
    faq_q6: "आयोजकों से कैसे संपर्क करें?",
    faq_a6: "नीचे दिए गए संपर्क बटन का उपयोग करें या फुटर पर हेल्पलाइन पर कॉल करें।",

    cta_title: "जैन गौरव यात्रा का हिस्सा बनें",
    cta_desc: "ऐप खोलें और यात्रा की हर अपडेट, घटना और पल से जुड़े रहें।",
    cta_open: "ऐप खोलें",
    cta_contact: "आयोजक से संपर्क करें",

    footer_tagline: "धर्म, सेवा और एकता के साथ — अहमदाबाद के 31 देरासरों की यात्रा का आपका साथी।",
    footer_quick: "त्वरित लिंक",
    footer_schedule: "यात्रा कार्यक्रम",
    footer_contact: "संपर्क",
    footer_get: "ऐप पाएं",
    footer_copy: "© 2026 जैन गौरव यात्रा। समुदाय के लिए भक्ति से बनाया गया।",
    footer_location: "अहमदाबाद, गुजरात",
  },

  gu: {
    appName: "જૈન ગૌરવ યાત્રા",
    welcome: "જય જિનેન્દ્ર",
    login: "લોગીન",
    logout: "લોગઆઉટ",
    phone: "મોબાઈલ નંબર",
    sendOtp: "OTP મોકલો",
    enterOtp: "OTP દાખલ કરો",
    verify: "ચકાસો",
    home: "હોમ",
    temples: "દેરાસર",
    map: "નકશો",
    bookings: "બુકિંગ",
    profile: "પ્રોફાઇલ",
    certificate: "પ્રમાણપત્ર",
    scanQr: "QR સ્કેન",
    progress: "યાત્રા પ્રગતિ",
    nextTemple: "આગળનું દેરાસર",
    visited: "દર્શન થયા",
    pending: "બાકી",
    viewDetails: "વિગતો",
    navigate: "રસ્તો",
    bookRickshaw: "રિક્ષા બુક કરો",
    bookVolunteer: "વોલન્ટિયર બુક કરો",
    history: "ઇતિહાસ",
    bookNow: "હમણાં બુક કરો",
    myBookings: "મારી બુકિંગ",
    confirm: "બુકિંગ કન્ફર્મ કરો",
    language: "ભાષા",
    admin: "એડમિન પોર્ટલ",
    openApp: "એપ ખોલો",
    save: "સાચવો",
    cancel: "રદ કરો",
    back: "પાછળ",
    loading: "લોડ થઈ રહ્યું છે...",
    history_title: "દેરાસરનો ઇતિહાસ",
    checkIn: "ચેક ઇન",
    alreadyVisited: "પહેલેથી દર્શન થયા",
    noBookings: "હજી કોઈ બુકિંગ નથી",
    moolNayak: "મૂળનાયક",
    established: "સ્થાપના",
    selectDate: "તારીખ",
    selectTime: "સમય",
    notes: "નોંધ (વૈકલ્પિક)",

    nav_about: "પરિચય",
    nav_features: "વિશેષતાઓ",
    nav_how: "કેવી રીતે કામ કરે છે",
    nav_faq: "પ્રશ્નોત્તર",

    hero_chip: "ધર્મ · સેવા · એકતા સાથે",
    hero_title_1: "જૈન ગૌરવ",
    hero_title_2: "યાત્રા",
    hero_desc: "જૈન ગૌરવ યાત્રા સાથે જોડાયેલા રહો — અમદાવાદના 31 દેરાસરોની દરેક અપડેટ, માર્ગ, કાર્યક્રમ, સેવા અને જાહેરાતો એક જ એપમાં સુંદર રીતે.",
    hero_cta_primary: "મેમ્બર એપ ખોલો",
    hero_cta_secondary: "યાત્રા વિગતો જુઓ",
    hero_badge_route: "લાઇવ રૂટ",
    hero_badge_route_sub: "2 મિનિટ પહેલા અપડેટ",
    hero_badge_announce: "નવી જાહેરાત",
    hero_badge_announce_sub: "આરતી સવારે 6:30 વાગ્યે",

    about_chip: "યાત્રા વિશે",
    about_title: "ભક્તિ, સેવા અને એકતાની યાત્રા",
    about_desc: "જૈન ગૌરવ યાત્રા એક આધ્યાત્મિક અને સામુદાયિક યાત્રા છે જે જૈન મૂલ્યો, સાંસ્કૃતિક ગૌરવ અને અહિંસા-સેવાના શાશ્વત સિદ્ધાંતોની ઉજવણી કરે છે.",
    about_b1: "એક આધ્યાત્મિક અને સામુદાયિક યાત્રા",
    about_b2: "જૈન મૂલ્યો, એકતા, સેવા અને સાંસ્કૃતિક ગૌરવનો પ્રચાર",
    about_b3: "ભક્તો, સ્વયંસેવકો અને આયોજકોને જોડવાનું",
    about_b4: "એપ દ્વારા તમામ યાત્રા માહિતી સહેલાઈથી ઉપલબ્ધ",
    about_overlay_kicker: "આધ્યાત્મિક યાત્રા",
    about_overlay_title: "સાથે ચાલો. સાથે પ્રાર્થના કરો.",

    features_chip: "આ એપ શા માટે વાપરવી",
    features_title: "યાત્રા માટે બધું, એક જ જગ્યાએ",
    features_desc: "હવે વિખરાયેલા સંદેશાઓની જરૂર નથી — દરેક અપડેટ, કાર્યક્રમ અને જાહેરાત તરત જ મળશે.",
    f1_title: "દેરાસર સૂચિ",
    f1_desc: "31 દેરાસરોની વિગતો, ઇતિહાસ અને મહત્ત્વ જુઓ.",
    f2_title: "ઇન્ટરએક્ટિવ નકશો",
    f2_desc: "રૂટ અને સ્થાન સાથે દેરાસરો સુધી સહેલાઈથી પહોંચો.",
    f3_title: "QR કોડ સ્કેન",
    f3_desc: "QR સ્કેન કરી દરેક દેરાસરે ઝડપી ચેક-ઇન કરો.",
    f4_title: "પ્રમાણપત્ર",
    f4_desc: "તમારી દર્શન યાત્રાની સિદ્ધિઓ અને પ્રમાણપત્રો મેળવો.",
    f5_title: "મનપસંદ",
    f5_desc: "તમારા પ્રિય દેરાસરોને ઝડપી ઍક્સેસ માટે સાચવો.",
    f6_title: "રિક્ષા માહિતી",
    f6_desc: "દેરાસરો વચ્ચે મુસાફરી માટે સહેલાઈથી રિક્ષા બુક કરો.",
    f7_title: "વોલન્ટિયર માહિતી",
    f7_desc: "યાત્રામાં મદદ માટે વોલન્ટિયર ગાઇડની વિનંતી કરો.",

    steps_chip: "કેવી રીતે કામ કરે છે",
    steps_title: "ત્રણ સરળ પગલાંમાં શરૂઆત કરો",
    s1_title: "એપ ખોલો",
    s1_desc: "આ વેબસાઇટથી સીધી જૈન ગૌરવ યાત્રા એપ ખોલો — કોઈ ઇન્સ્ટોલેશનની જરૂર નથી.",
    s2_title: "એપ એક્સપ્લોર કરો",
    s2_desc: "દેરાસર, વોલન્ટિયર અને રિક્ષા જુઓ — તરત, સાઇન-અપ વગર.",
    s3_title: "જોડાયેલા રહો",
    s3_desc: "દરેક યાત્રા અપડેટ, કાર્યક્રમ, માર્ગ અને જાહેરાત તરત જ મેળવો.",

    preview_chip: "એપ સ્ક્રીન ઝલક",
    preview_title: "એપની અંદર એક નજર",
    preview_desc: "યાત્રા અનુભવને જીવંત બનાવતી મુખ્ય સ્ક્રીનની ઝલક જુઓ.",
    screen_home: "હોમ ડેશબોર્ડ",
    screen_directory: "દેરાસર સૂચિ",
    screen_certificate: "પ્રમાણપત્ર",
    screen_bookings: "મારી બુકિંગ",
    screen_volunteer: "વોલન્ટિયર ગાઇડ",

    benefits_chip: "વપરાશકર્તાઓ માટે લાભ",
    benefits_title: "તમારી સહભાગીતા માટે બનાવાયેલ",
    benefits_desc: "આયોજકોથી લઈને પહેલી વખતના ભક્તો સુધી — સરળ, ઝડપી અને વિશ્વસનીય.",
    b1: "વિખરાયેલા WhatsApp સંદેશાઓ પર નિર્ભર રહેવાની જરૂર નથી",
    b2: "બધી માહિતી એક જ જગ્યાએ ઉપલબ્ધ",
    b3: "આયોજકો અને સહભાગીઓ વચ્ચે વધુ સારું સંકલન",
    b4: "સમુદાય સાથે રીયલ-ટાઇમ સંવાદ",
    b5: "રૂટ, સમય અને અપડેટ સહેલાઈથી મળશે",
    b6: "જોડાયેલા જૈન સમુદાય બનાવવામાં મદદ",

    banner_title: "આસ્થા, સેવા અને ભક્તિમાં એકજૂથ",
    banner_desc: "જૈન ગૌરવ યાત્રા એપ સમુદાયને એકસાથે લાવવા માટે બનાવાઈ છે, જેથી સહભાગીતા, સંવાદ અને સંકલન સરળ અને વ્યવસ્થિત બને.",

    test_chip: "સમુદાયના અવાજ",
    test_title: "અમારા સમુદાય દ્વારા પ્રિય",
    t1_quote: "હવે કોઈ આરતી કે પ્રવચન ચૂકતું નથી. હોમ સ્ક્રીન પર બધું સુંદર રીતે ગોઠવેલું છે.",
    t1_name: "રાકેશ શાહ",
    t1_role: "યાત્રા સહભાગી",
    t2_quote: "સેવા નોંધણી સરળ હતી. સૂચનાઓથી અમારી ટીમ આખો દિવસ વ્યવસ્થિત રહે છે.",
    t2_name: "પ્રિયા મહેતા",
    t2_role: "સ્વયંસેવક",
    t3_quote: "હજારો ભક્તો સાથે વાતચીત હવે ખૂબ સરળ છે. સમુદાય માટે સાચું વરદાન.",
    t3_name: "મહેન્દ્રભાઈ જૈન",
    t3_role: "આયોજક",

    faq_chip: "પ્રશ્નોત્તર",
    faq_title: "વારંવાર પૂછાતા પ્રશ્નો",
    faq_q1: "જૈન ગૌરવ યાત્રા એપ શું છે?",
    faq_a1: "આ જૈન ગૌરવ યાત્રાનું સત્તાવાર સાથી એપ છે — અમદાવાદના 31 દેરાસરોની દર્શન યાત્રા, માર્ગ, કાર્યક્રમ, સેવા અને જાહેરાતો.",
    faq_q2: "આ એપ કોણ વાપરી શકે છે?",
    faq_a2: "જૈન સમુદાયના ભક્તો, સહભાગીઓ, સ્વયંસેવકો, આયોજકો અને શુભચિંતકો બધા વાપરી શકે છે.",
    faq_q3: "મને યાત્રા અપડેટ કેવી રીતે મળશે?",
    faq_a3: "બધી રીયલ-ટાઇમ જાહેરાતો એપમાં અને તાત્કાલિક પુશ સૂચનાઓ તરીકે દેખાશે.",
    faq_q4: "શું હું સ્વયંસેવક તરીકે નોંધણી કરી શકું?",
    faq_a4: "હા, વોલન્ટિયર વિભાગમાં તમે નોંધણી કરી તમારું દેરાસર અને સમય પસંદ કરી શકો.",
    faq_q5: "શું એપ વાપરવી મફત છે?",
    faq_a5: "હા, સમગ્ર જૈન સમુદાય માટે આ એપ સંપૂર્ણપણે મફત છે.",
    faq_q6: "આયોજકોનો સંપર્ક કેવી રીતે કરવો?",
    faq_a6: "નીચેના સંપર્ક બટનનો ઉપયોગ કરો અથવા ફૂટરમાં હેલ્પલાઇન પર કોલ કરો.",

    cta_title: "જૈન ગૌરવ યાત્રાનો ભાગ બનો",
    cta_desc: "એપ ખોલો અને યાત્રાની દરેક અપડેટ, ઘટના અને પળ સાથે જોડાયેલા રહો.",
    cta_open: "એપ ખોલો",
    cta_contact: "આયોજકનો સંપર્ક કરો",

    footer_tagline: "ધર્મ, સેવા અને એકતા સાથે — અમદાવાદના 31 દેરાસરોની યાત્રાનો તમારો સાથી.",
    footer_quick: "ઝડપી લિંક",
    footer_schedule: "યાત્રા કાર્યક્રમ",
    footer_contact: "સંપર્ક",
    footer_get: "એપ મેળવો",
    footer_copy: "© 2026 જૈન ગૌરવ યાત્રા. સમુદાય માટે ભક્તિથી બનાવાયેલ.",
    footer_location: "અમદાવાદ, ગુજરાત",
  },
} as const;

export type DictKey = keyof typeof DICT["en"];

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: DictKey) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("en");
  useEffect(() => {
    const saved = (typeof window !== "undefined" && (localStorage.getItem("lang") as Lang)) || "en";
    if (saved === "en" || saved === "hi" || saved === "gu") {setLangState(saved);}
  }, []);
  const setLang = (l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") {localStorage.setItem("lang", l);}
  };
  const t = (k: DictKey) => (DICT[lang] as any)[k] || (DICT.en as any)[k];
  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const c = useContext(Ctx);
  if (!c) {throw new Error("useI18n outside provider");}
  return c;
}

export const LANG_LABELS: Record<Lang, string> = { en: "English", hi: "हिन्दी", gu: "ગુજરાતી" };

export function pickName(t: { name_en: string; name_hi: string; name_gu: string }, lang: Lang) {
  return lang === "gu" ? t.name_gu || t.name_en : lang === "hi" ? t.name_hi || t.name_en : t.name_en;
}
export function pickHistory(t: { history_en: string; history_hi: string; history_gu: string }, lang: Lang) {
  return lang === "gu" ? t.history_gu || t.history_en : lang === "hi" ? t.history_hi || t.history_en : t.history_en;
}
